import React from 'react'
import { withRouter } from 'react-router-dom'

import { BaseForm, Input, Select, SingleDatePicker } from 'components/Form'
import { Dialog, Popup } from 'components/Popup'
import SuccessIcon from 'components/Popup/SuccessPopup/success-icon.svg'
import { SALES_ORDERS_PAY_URL } from 'config/app'

import { getMessage } from 'lib/translator'
import _get from 'lodash.get'
import { getCalculatedItemData } from 'lib/commonlyused/orderCalculations'
import API from 'lib/api'
import RedeliveryTable from './RedeliveryTable'
import { createOrderPayload, PAYMENT_MODES, TIME_SLOT_OPTIONS } from './constant'
import '../style.scss'

const toNum = (v) => {
  const n = parseFloat(v)
  return Number.isFinite(n) ? n : 0
}
const toFixed2 = (n) => Number(n || 0).toFixed(2)
const safeString = (v) => (v === 0 || v ? String(v) : '')
export const isMkpItem = (item) => {
  return item?.vendor_code > 0 && item?.clientItemId < 90000000
}

class ReDeliveryForm extends BaseForm {
  constructor(props) {
    super(props)
    this.state = {
      values: {
        items: [],
        adjustment: '',
        selectedCardId: '',
      },
      ui: {
        adjustmentFocused: false,
      },
      showConfirmation: false,
      showErrorModal: false,
      dataToSubmit: null,
      showSuccessModal: false,
      newOrderId: null,
      responseOrder: null,
      paymentMethods: [],
      redeliveryDate: null,
      redeliveryTimeSlot: null,
      errorMessage: null
    }

    this.onSubmit = this.onSubmit.bind(this)
    this.handleQuantityChange = this.handleQuantityChange.bind(this)
    this.handleAdjustmentChange = this.handleAdjustmentChange.bind(this)
    this.handleAdjustmentBlur = this.handleAdjustmentBlur.bind(this)
    this.handleQuantityInputChange = this.handleQuantityInputChange.bind(this)
    this.handleCardChange = this.handleCardChange.bind(this)
    this.closeConfirmation = this.closeConfirmation.bind(this)
    this.closeSuccessModal = this.closeSuccessModal.bind(this)
    this.viewNewOrder = this.viewNewOrder.bind(this)
    this.getListCardApi = this.getListCardApi.bind(this)
    this.cardOptions = this.cardOptions.bind(this)
    this.handleAdjustmentFocus = this.handleAdjustmentFocus.bind(this)
  }

  componentDidMount() {
    const getQty = (v) => (v == null || v === '' ? NaN : parseFloat(v))

    const isFullyFulfilled = (item) => {
      const od = item?.orderDetails ?? {}
      const ordered = getQty(od.orderedQuantity ?? od.originalQuantity)
      const delivered = getQty(od.deliveredQuantity ?? od.finalQuantity)

      if (Number.isNaN(ordered) || Number.isNaN(delivered)) {
        return false
      }
      return Math.abs(ordered - delivered) < 1e-9
    }

    this.getListCardApi()
    const orders = _get(this.props, 'saleOrderData.orders', [])
    const items = (orders || [])
      .flatMap(
        (order) =>
          order?.seller === null && order?.status === 'COMPLETED' && order.items
      )
      .filter((item) => {
        return (
          !_get(item, 'orderDetails.substitutedItemId') &&
          (item?.vendor_code === 0 || isMkpItem(item)) &&
          !isFullyFulfilled(item)
        )
      })
      .map((item) => {
        const { fixedDecimalPlace } = getCalculatedItemData(
          { ...item },
          'saleOrder'
        )

        const ordered = toNum(item?.orderDetails?.orderedQuantity)
        const delivered = toNum(item?.orderDetails?.deliveredQuantity)

        const available = Math.max(0, ordered - delivered)

        return {
          ...item,
          ...item.orderDetails,
          redeliveryQty:
            fixedDecimalPlace > 0
              ? Number(available.toFixed(fixedDecimalPlace))
              : available,
          _availableForRedelivery: available,
        }
      })

    this.setState((prev) => ({
      values: {
        ...prev.values,
        items,
      },
    }))
  }

  cardOptions(cards) {
    return cards.map((card) => {
      const last4 = card.number.slice(-4)
      return {
        value: card.id,
        text: `${card.type} (${last4})`,
      }
    })
  }

  async getListCardApi() {
    const { saleOrderData } = this.props
    try {
      const url = `${SALES_ORDERS_PAY_URL}/ListAllCustomerPaymentCards`
      const api = new API({ url })
      const response = await api.post({
        customerUid: saleOrderData?.customer?.uid,
      })
      if (response && Array.isArray(response?.results)) {
        const cardOptions = this.cardOptions(response?.results || [])
        const firstCardId =
          response?.results?.length > 0 ? response?.results[0].id : null

        this.setState((prev) => ({
          paymentMethods: cardOptions,
          values: {
            ...prev.values,
            selectedCardId: firstCardId,
          },
        }))
      } else {
        this.setState((prev) => ({
          paymentMethods: [],
          values: {
            ...prev.values,
            selectedCardId: null,
          },
        }))
      }
    } catch (error) {
      console.error('Failed to fetch card listing:', error)
    }
  }

  handleConfirmSubmit = async () => {
    const { dataToSubmit } = this.state
    try {
      const url = `${SALES_ORDERS_PAY_URL}/CheckoutOrder`
      const api = new API({ url })
      const response = await api.post(dataToSubmit)
      if (response) {
        this.setState((prevState) => ({
          ...prevState,
          showSuccessModal: true,
          newOrderId: response.orderNo,
          responseOrder: response,
        }))
      }
    } catch (error) {
      this.setState((prevState) => ({ ...prevState, showErrorModal: true }))
      console.error('Failed to submit redelivery order:', error)
    } finally {
      this.setState({ showConfirmation: false, dataToSubmit: null })
    }
  }

  onSubmit(data) {
    const { redeliveryDate, redeliveryTimeSlot } = this.state
    const { saleOrderData, referenceNumber } = this.props
    const { items, adjustment, selectedCardId } = data

    if (!redeliveryDate || !redeliveryTimeSlot) {
      this.setState({ errorMessage: "Date and time slot cannot be empty.", showErrorModal: true })
    } else {
      const partialDiscountMethods = []
  
      const amount = String(Math.abs(parseFloat(adjustment || 0)))
  
      if (amount !== '0') {
        partialDiscountMethods.push({
          client: {
            system: 'CLIENT_SYSTEM_SELF_MANAGED',
            selfManagedSystem: 'BACK_OFFICE',
          },
          amount,
          identifier: 'Adjustment',
          isGstDeductible: true,
          selfManagedType: 'DISCOUNT',
        })
      }
  
      const redeliveryItems = items
        .filter((item) => Number(item.redeliveryQty) > 0)
        .map((item) => {
          const offer =
            Array.isArray(item.offer) && item.offer.length ? item.offer[0] : null
          const hasDiscount = offer && offer.discount != null
          /* istanbul ignore next */
          const isGst = isMkpItem(item) ?? true

          return {
            sellerVendorCode: '0',
            productId: item.productId ?? item.id,
            sku: item.clientItemId,
            ean: item.ean ?? item.barcodes?.[0] ?? '',
            name: item.name,
            subtitle: item.subtitle ?? '',
            imageUrl: item.imageUrl ?? item.images?.[0] ?? '',
            unitPrice: toFixed2(item.mrp),
            orderedQuantity: safeString(item.redeliveryQty),
  
            ...(hasDiscount && {
              discounts: [
                {
                  unitAmount: Number(offer.discount).toFixed(2).toString(),
                  id: safeString(offer.id),
                  name: offer.description ?? '',
                  type: 'DISCOUNT',
                  isGstDeductible: true,
                },
              ],
            }),
  
            metadata: item.metaData ?? {},
            isSubjectToGst: isGst,
          }
        })
  
      const payload = createOrderPayload({
        referenceNumber,
        saleOrderData,
        redeliveryItems,
        partialDiscountMethods,
        selectedCardId,
        redeliveryDate,
        redeliveryTimeSlot,
      })
  
      this.setState({ showConfirmation: true, dataToSubmit: payload })
    }

  }

  closeConfirmation() {
    this.setState({ showConfirmation: false, dataToSubmit: null })
  }

  closeSuccessModal() {
    this.setState({ showSuccessModal: false, newOrderId: null })
  }

  viewNewOrder = () => {
    const soId = this.state.responseOrder?.salesOrderId

    this.props.history.push({
      pathname: `/customer-support/sales-orders/${soId}`,
    })
  }

  handleCardChange(value) {
    this.setState((prev) => ({
      values: { ...prev.values, selectedCardId: value },
    }))
  }

  handleQuantityChange = (index, delta) => {
    this.setState((prev) => {
      const items = [...prev.values.items]
      const it = { ...items[index] }

      const ordered = toNum(
        it.orderDetails?.orderedQuantity ?? it.orderedQuantity
      )
      const delivered = toNum(
        it.orderDetails?.deliveredQuantity ?? it.deliveredQuantity
      )
      const status = it.orderDetails?.status ?? it.status

      const available =
        it._availableForRedelivery ??
        (status === 'CANCELLED' ? 0 : Math.max(0, ordered - delivered))

      const curr = toNum(it.redeliveryQty)
      const next = Math.max(0, Math.min(available, curr + delta))
      if (next === curr) {
        return null
      }

      it.redeliveryQty = next
      items[index] = it
      return { values: { ...prev.values, items } }
    })
  }

  handleQuantityInputChange = (value, index) => {
    const items = [...this.state.values.items]
    const current = { ...items[index] }

    if (value === '') {
      current.redeliveryQty = ''
    } else {
      const entered = parseInt(value, 10)
      if (Number.isNaN(entered)) {
        return
      }

      const delivered =
        Number(
          current.deliveredQuantity ?? current.orderDetails?.deliveredQuantity
        ) || 0
      const ordered =
        Number(
          current.orderedQuantity ?? current.orderDetails?.orderedQuantity
        ) || 0
      const maxAllowed = Math.max(0, ordered - delivered)

      if (entered > maxAllowed) {
        return
      }

      current.redeliveryQty = entered
    }

    items[index] = current
    this.setState((prev) => ({ values: { ...prev.values, items } }))
  }

  formatDisplay = (raw) => {
    if (!raw) {
      return '$0.00'
    }
    return `-$${toNum(raw).toFixed(2)}`
  }

  // typing handler (e is already the raw string)
  handleAdjustmentChange = (e) => {
    let value = String(e || '').replace(/[^0-9.]/g, '')

    // allow only a single dot
    const parts = value.split('.')
    if (parts.length > 2) {
      value = `${parts.shift()}.${parts.join('')}`
    }

    this.setState((prev) => ({
      values: { ...prev.values, adjustment: value },
    }))
  }

  // blur: lock to 2 decimals (if empty, keep empty so default shows)
  handleAdjustmentBlur = () => {
    const raw = this.state.values.adjustment
    if (!raw) {
      // also mark as not focused
      this.setState((prev) => ({
        ui: { ...prev.ui, adjustmentFocused: false },
      }))
      return
    }
    const fixed = toNum(raw).toFixed(2)
    this.setState((prev) => ({
      values: { ...prev.values, adjustment: fixed },
      ui: { ...prev.ui, adjustmentFocused: false },
    }))
  }

  // focus: switch to "editing" mode
  handleAdjustmentFocus() {
    this.setState((prev) => ({
      ui: { ...prev.ui, adjustmentFocused: true },
    }))
  }

  calculateSubtotal() {
    return this.state.values.items.reduce((total, item) => {
      const mrp = Number(item.mrp) || 0
      const discount = Number(item.discount) || 0
      const qty = Number(item.redeliveryQty) || 0

      const unitPrice = mrp - discount
      const lineTotal = Math.round(unitPrice * qty * 100) / 100

      return total + lineTotal
    }, 0)
  }

  calculateTotal() {
    const subtotal = this.calculateSubtotal()

    const raw = this.state.values.adjustment
    const adjustment = raw ? -parseFloat(raw) : 0

    const total = subtotal + adjustment

    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
    })

    return formatter.format(total || 0)
  }

  render() {
    const paymentMode =
      this.props?.saleOrderData?.payments?.[0]?.mode || 'UNKNOWN'

    const { Form } = this.components
    const { SubmitButton } = this.buttons
    const { items, selectedCardId } = this.state.values
    const {
      showConfirmation,
      showSuccessModal,
      newOrderId,
      showErrorModal,
      redeliveryDate,
      redeliveryTimeSlot,
    } = this.state

    const subtotal = this.calculateSubtotal()
    const total = this.calculateTotal()
    const totalRedeliveryQty = items.reduce(
      (sum, item) => sum + (toNum(item.redeliveryQty) || 0),
      0
    )

    const isDisabled = totalRedeliveryQty === 0
    return (
      <div className="re-delivery">
        <Form>
          <div className="row">
            <div className="col-6">
              <h3>{getMessage('order.details.redelivery.text')}</h3>
            </div>
          </div>

          <RedeliveryTable
            items={items}
            onCardChange={this.handleCardChange}
            onQuantityChange={this.handleQuantityChange}
            onQuantityInputChange={this.handleQuantityInputChange}
          />

          <hr style={{ padding: 0 }} />

          <div className="row mb">
            <div className="col-6">
              <div
                style={{ display: 'flex', alignItems: 'center', width: '100%' }}
              >
                <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                  Payment Method
                </span>

                <div style={{ flex: 1 }}>
                  {paymentMode === 'ONLINE' ? (
                    <Select
                      value={selectedCardId}
                      onChange={this.handleCardChange}
                      options={this.state.paymentMethods}
                    />
                  ) : (
                    <span className="input-left">
                      <Input value={PAYMENT_MODES[paymentMode]} readOnly />
                    </span>
                  )}
                </div>
              </div>
              <div className="date-time-slot-container">
                <div
                  style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                    {getMessage('saleOrder.form.redelivery.date')}
                  </span>
                  <SingleDatePicker
                    placeholder={getMessage('saleOrder.form.redelivery.date.placeholder')}
                    name="redelivery-date-select"
                    testid="redelivery-date-select"
                    className="date-container"
                    enableToday
                    value={redeliveryDate}
                    onChange={(value) => this.setState({ redeliveryDate: value })}
                  />
                </div>

                <div
                  style={{ display: 'flex', alignItems: 'center', width: '100%' }}
                >
                  <span style={{ marginRight: 8, whiteSpace: 'nowrap' }}>
                    {getMessage('saleOrder.form.redelivery.timeslot')}
                  </span>
                  <Select
                    placeholder={getMessage('saleOrder.form.redelivery.timeslot.placeholder')}
                    name="redelivery-time-slot-select"
                    testid="redelivery-time-slot-select"
                    className="time-slot-container"
                    options={TIME_SLOT_OPTIONS}
                    value={redeliveryTimeSlot}
                    onChange={(value) => this.setState({ redeliveryTimeSlot: value })}
                  />
                </div>


              </div>
            </div>
            <div className="col-6">
              <span style={{ float: 'right' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 8 }}>
                    {getMessage('saleOrder.details.subtotal')}
                  </span>
                  <Input value={`$${subtotal.toFixed(2)}`} readOnly />
                </div>
              </span>
            </div>
          </div>

          <div className="row">
            <div className="col-6" />
            <div className="col-6">
              <span style={{ float: 'right' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ marginRight: 8 }}>Adjustment</span>
                  <Input
                    type="text"
                    inputMode="decimal"
                    testid="adjustment-input"
                    value={
                      this.state.ui.adjustmentFocused
                        ? `-$${this.state.values.adjustment || ''}`
                        : this.formatDisplay(this.state.values.adjustment)
                    }
                    onFocus={this.handleAdjustmentFocus}
                    onChange={(e) => this.handleAdjustmentChange(e)}
                    onBlur={this.handleAdjustmentBlur}
                  />
                </div>
              </span>
            </div>
          </div>

          <div className="row mt mb">
            <div className="col-6" />
            <div className="col-6">
              <strong style={{ float: 'right' }} data-testid="order-total">
                {getMessage('saleOrder.details.total')}: {total}
              </strong>
            </div>
          </div>

          <div className="form-actions">
            <button
              data-testid="back-button"
              className="button"
              type="button"
              onClick={() =>
                this.props.history.push(
                  `/customer-support/sale-orders/${this.props.referenceNumber}`
                )
              }
            >
              Back
            </button>

            <SubmitButton
              disabled={isDisabled}
              className="primary button"
              type="submit"
            >
              {getMessage('media.add.text')}
            </SubmitButton>

            {showConfirmation && (
              <Dialog
                show={showConfirmation}
                title={getMessage('apphome.confirmation')}
                information={getMessage('order.details.redelivery.submit')}
                closeText={getMessage('apphome.cancel')}
                onOk={this.handleConfirmSubmit}
                close={this.closeConfirmation}
                okText={getMessage('rewardPage.dialogs.disable.okText')}
              />
            )}
            {showErrorModal && (
              <Dialog
                show={showErrorModal}
                title="Try Again"
                information={this.state.errorMessage || getMessage(
                  'order.details.redelivery.create.error'
                )}
                closeText={getMessage('apphome.cancel')}
                close={() =>
                  this.setState((prevState) => ({
                    ...prevState,
                    showErrorModal: false,
                  }))
                }
                okText={getMessage('rewardPage.dialogs.disable.okText')}
              />
            )}

            {showSuccessModal && (
              <Popup
                show={showSuccessModal}
                heading="Redelivery Order Created"
                close={() =>
                  this.props.history.push(
                    `/customer-support/sale-orders/${this.props.referenceNumber}`
                  )
                }
              >
                <div className="confirm-msg-success">
                  <p>Redelivery order: {newOrderId}</p>

                  <p>
                    Successfully created{' '}
                    <img
                      style={{ width: 24, height: 24 }}
                      src={SuccessIcon}
                      alt="success icon"
                    />
                  </p>
                </div>
                <div className="button-wrapper">
                  <button
                    className="button button-white"
                    type="button"
                    onClick={() =>
                      this.props.history.push(
                        `/customer-support/sale-orders/${this.props.referenceNumber}`
                      )
                    }
                  >
                    Cancel
                  </button>
                  <button
                    className="button primary"
                    type="button"
                    onClick={() => this.viewNewOrder()}
                  >
                    View
                  </button>
                </div>
              </Popup>
            )}
          </div>
        </Form>
      </div>
    )
  }
}

export default withRouter(ReDeliveryForm)